/**
 * NotificationTemplateEditor — board-level notification template customization.
 *
 * Compact list view in board settings. Clicking an event type opens a modal
 * with the full editor (subject, body, variable chips, live preview).
 *
 * Requirements: 4.1–4.8
 */

import React, { useCallback, useEffect, useState } from 'react';
import * as api from '../../services/api-client';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../ui/dialog';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface TemplateData {
  readonly eventType: string;
  readonly subject: string;
  readonly body: string;
  readonly isCustom: boolean;
  readonly variables: string[];
}

interface TemplatesResponse {
  readonly data: { templates: TemplateData[] };
}

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const EVENT_LABELS: Record<string, string> = {
  'card.assigned': 'Card Assigned',
  'board.invite.created': 'Board Invitation',
  'comment.mention': 'Comment Mention',
  'card.due.approaching': 'Due Date Reminder',
  'card.moved': 'Card Moved',
};

const SAMPLE_DATA: Record<string, string> = {
  actorName: 'John Doe',
  recipientName: 'Jane Smith',
  link: 'https://flow.devtechnology.app/boards/example/cards/example',
  boardTitle: 'Sprint Board',
  cardTitle: 'Implement user authentication',
  dueDate: 'August 5, 2026',
  sourceListTitle: 'In Progress',
  targetListTitle: 'Done',
};

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------

export interface NotificationTemplateEditorProps {
  readonly boardId: string;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function NotificationTemplateEditor({ boardId }: NotificationTemplateEditorProps): React.ReactElement {
  const [templates, setTemplates] = useState<TemplateData[]>([]);
  const [loading, setLoading] = useState(true);
  const [editingEvent, setEditingEvent] = useState<string | null>(null);
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  // Fetch templates on mount
  useEffect(() => {
    async function load() {
      setLoading(true);
      try {
        const response = await api.get<TemplatesResponse>(`/v1/boards/${boardId}/notification-templates`);
        setTemplates(response.data.templates);
      } catch {
        // Failed to load
      } finally {
        setLoading(false);
      }
    }
    load();
  }, [boardId]);

  const openEditor = useCallback((eventType: string) => {
    const template = templates.find((t) => t.eventType === eventType);
    if (template) {
      setSubject(template.subject);
      setBody(template.body);
      setMessage(null);
      setEditingEvent(eventType);
    }
  }, [templates]);

  const closeEditor = useCallback(() => {
    setEditingEvent(null);
    setMessage(null);
  }, []);

  const handleSave = useCallback(async () => {
    if (!editingEvent) return;
    setSaving(true);
    setMessage(null);
    try {
      await api.put(`/v1/boards/${boardId}/notification-templates/${editingEvent}`, { subject, body });
      setTemplates((prev) =>
        prev.map((t) => t.eventType === editingEvent ? { ...t, subject, body, isCustom: true } : t),
      );
      setMessage({ type: 'success', text: 'Template saved' });
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : 'Failed to save template';
      setMessage({ type: 'error', text: msg });
    } finally {
      setSaving(false);
    }
  }, [boardId, editingEvent, subject, body]);

  const handleReset = useCallback(async () => {
    if (!editingEvent) return;
    setSaving(true);
    setMessage(null);
    try {
      await api.del(`/v1/boards/${boardId}/notification-templates/${editingEvent}`);
      const response = await api.get<TemplatesResponse>(`/v1/boards/${boardId}/notification-templates`);
      setTemplates(response.data.templates);
      const updated = response.data.templates.find((t) => t.eventType === editingEvent);
      if (updated) {
        setSubject(updated.subject);
        setBody(updated.body);
      }
      setMessage({ type: 'success', text: 'Reset to default' });
    } catch {
      setMessage({ type: 'error', text: 'Failed to reset template' });
    } finally {
      setSaving(false);
    }
  }, [boardId, editingEvent]);

  const insertVariable = useCallback((variable: string) => {
    setBody((prev) => prev + `{{${variable}}}`);
  }, []);

  const activeTemplate = templates.find((t) => t.eventType === editingEvent);
  const availableVariables = activeTemplate?.variables ?? [];
  const previewSubject = interpolatePreview(subject);
  const previewBody = interpolatePreview(body);

  if (loading) {
    return <p className="text-sm text-muted-foreground py-2">Loading...</p>;
  }

  return (
    <>
      {/* Compact list */}
      <ul className="space-y-1">
        {templates.map((t) => (
          <li key={t.eventType}>
            <button
              type="button"
              onClick={() => openEditor(t.eventType)}
              className="w-full flex items-center justify-between px-2 py-1.5 rounded text-left text-xs hover:bg-muted transition-colors"
            >
              <span className="text-foreground">{EVENT_LABELS[t.eventType] ?? t.eventType}</span>
              {t.isCustom ? (
                <span className="text-[10px] px-1.5 py-0.5 rounded bg-green-100 text-green-700">Custom</span>
              ) : (
                <span className="text-[10px] text-muted-foreground">Default</span>
              )}
            </button>
          </li>
        ))}
      </ul>

      {/* Editor Modal */}
      <Dialog open={editingEvent !== null} onOpenChange={(open) => { if (!open) closeEditor(); }}>
        <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              Edit Template: {editingEvent ? EVENT_LABELS[editingEvent] ?? editingEvent : ''}
            </DialogTitle>
          </DialogHeader>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mt-4">
            {/* Left: Inputs */}
            <div className="space-y-3">
              <div>
                <label htmlFor="template-subject" className="block text-xs font-medium text-muted-foreground mb-1">
                  Subject
                </label>
                <input
                  id="template-subject"
                  type="text"
                  value={subject}
                  onChange={(e) => setSubject(e.target.value)}
                  className="w-full rounded-md border border-border bg-background px-3 py-1.5 text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-ring"
                />
              </div>

              <div>
                <label htmlFor="template-body" className="block text-xs font-medium text-muted-foreground mb-1">
                  Body (HTML)
                </label>
                <textarea
                  id="template-body"
                  value={body}
                  onChange={(e) => setBody(e.target.value)}
                  rows={8}
                  className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm text-foreground font-mono focus:outline-none focus:ring-2 focus:ring-ring"
                />
              </div>

              {/* Variable chips */}
              <div>
                <span className="text-xs text-muted-foreground">Insert variable:</span>
                <div className="flex flex-wrap gap-1 mt-1">
                  {availableVariables.map((v) => (
                    <button
                      key={v}
                      type="button"
                      onClick={() => insertVariable(v)}
                      className="px-2 py-0.5 text-[11px] rounded border border-border bg-muted text-muted-foreground hover:bg-accent hover:text-foreground"
                    >
                      {`{{${v}}}`}
                    </button>
                  ))}
                </div>
              </div>
            </div>

            {/* Right: Preview */}
            <div>
              <span className="block text-xs font-medium text-muted-foreground mb-1">Preview</span>
              <div className="rounded-md border border-border bg-background p-3 space-y-2 max-h-64 overflow-y-auto">
                <p className="text-xs font-semibold text-foreground">{previewSubject || '(no subject)'}</p>
                <div
                  className="text-xs text-muted-foreground"
                  dangerouslySetInnerHTML={{ __html: previewBody || '<em>(no body)</em>' }}
                />
              </div>
            </div>
          </div>

          {message && (
            <p className={`text-xs mt-2 ${message.type === 'success' ? 'text-green-600' : 'text-destructive'}`}>
              {message.text}
            </p>
          )}

          <DialogFooter className="mt-4">
            {activeTemplate?.isCustom && (
              <button
                type="button"
                onClick={handleReset}
                disabled={saving}
                className="rounded-md border border-border px-3 py-1.5 text-xs font-medium text-muted-foreground hover:text-foreground disabled:opacity-50"
              >
                Reset to Default
              </button>
            )}
            <button
              type="button"
              onClick={closeEditor}
              className="rounded-md border border-border px-3 py-1.5 text-xs font-medium text-foreground hover:bg-accent"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={handleSave}
              disabled={saving}
              className="rounded-md bg-primary px-3 py-1.5 text-xs font-medium text-primary-foreground hover:bg-primary/90 disabled:opacity-50"
            >
              {saving ? 'Saving...' : 'Save'}
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

// ---------------------------------------------------------------------------
// Preview Helper
// ---------------------------------------------------------------------------

function interpolatePreview(template: string): string {
  return template.replace(/\{\{(\w+)\}\}/g, (_, variable: string) => {
    return SAMPLE_DATA[variable] ?? `{{${variable}}}`;
  });
}
